<script setup lang="ts">
  import { useFlowConfigValidation } from '@/composables/validation/use-flow-config-validation'
  import router from '@/router'
  import { useFlowConfigStore } from '@/store/flow-config.store'
  import { createEmptyConfig, type CreateFlowConfig } from '@/types/flow-config-type'
  import { FormInput, Modal, UiButton, useToastStore } from '@logistiek-zonder-papier/lzp-library'
  import { isAxiosError } from 'axios'
  import type { UUID } from 'node:crypto'
  import { ref } from 'vue'

  const configStore = useFlowConfigStore()
  const toastStore = useToastStore()

  const config = ref<CreateFlowConfig>(createEmptyConfig())

  const { validate, visibleErrors: errors } = useFlowConfigValidation(config)

  const isSubmitting = ref(false)

  const { subAppId } = defineProps<{ subAppId: UUID }>()

  const show = defineModel<boolean>('show', { default: false })

  async function submit() {
    if (!validate()) {
      return
    }
    isSubmitting.value = true
    try {
      await configStore.createFlow(
        config.value.type,
        subAppId,
        config.value.label,
        config.value.description
      )
    } catch (error) {
      if (isAxiosError(error)) {
        if (error.response?.status === 409) {
          errors.value.type = 'Dit type bestaat al voor deze sub app'
          isSubmitting.value = false
          return
        }
      }
      throw error
    }

    toastStore.success('Flow type is succesvol aangemaakt')

    await router.push({
      name: 'configurator',
      params: { subAppId: subAppId, type: config.value.type },
    })
  }
</script>

<template>
  <Modal v-model:show="show">
    <template #header>
      <h3>Nieuwe flow toevoegen</h3>
    </template>
    <FormInput label="Flow Type" type="text" v-model="config.type" :error="errors.type" />

    <FormInput label="Label" type="text" v-model="config.label" :error="errors.label" />

    <FormInput
      label="Beschrijving"
      type="text"
      v-model="config.description"
      :error="errors.description"
    />
    <template #footer="{ close }">
      <UiButton variant="default" type="submit" label="Annuleren" @click="close" />
      <UiButton
        variant="prominent"
        type="submit"
        label="Toevoegen"
        :disabled="isSubmitting"
        @click="submit"
      />
    </template>
  </Modal>
</template>

<style lang="css" scoped></style>
